import React, { useCallback, useId, useState } from 'react'
import { compose } from 'redux'
import { Link } from 'react-router-dom'
import { withTranslator } from '../HOCComponents/withTranslator'
import withLocale from '../HOCComponents/withLocale'
import { UIStyles, surfaces, baseTheme } from '../../theme'
import styled, { css } from 'styled-components'
import LanguageButtons from '../../../src/components/UserComponents/LanguageButtons'
import { ROUTES } from '../../constants'
import Routing from '../../Routing'
import { Collapse, ListItemButton, ListItemIcon, ListItemText, List, ClickAwayListener } from '@mui/material'
import themeColors from '@layers-frontend/commons/styles/themeColors'
import { pageViewed } from '../analytics/analytics'
import { getDocumentationLocale } from '@layers-frontend/commons/helpers/getDocumentationLocale'
import { isCsatAvailable } from '../../feedback/csatAvailability'
import { StyledSwitchComponent } from '../UIComponents/StyledSwitch/StyledSwitch'
import { areLayiNotificationsOn, isLayiNotificationsSupported, unhideLayiNotificationsSetup } from '../../chatbot/layiNotifications'
import useLayiNotificationsSwitch from '../../chatbot/useLayiNotificationsSwitch'

import UserConfiguration from './UserConfiguration'

const { LOG_OUT_ROUTE } = ROUTES
const freshdeskUrl = process.env.REACT_APP_FRESHDESK_URL
const layersStatusUrl = process.env.REACT_APP_LAYERS_STATUS_URL

const topBarHeight = '60px'
const menuWidth = '258px'
// Docked under the top bar like the field panel: glass, with the mint contour on the free left and bottom edges.
// The glass lives on a pseudo-element behind the content: a backdrop-filter on the wrapper itself would make it the
// containing block of the language menus, which render in place with position: fixed, and push them off screen.
// Taller than the window, the list scrolls inside the menu, which then ends at the window's bottom like the field
// panel; the scroll never chains to the page. Rows keep the full menu width and slide under the scrollbar (100% is
// the list minus its scrollbar), so it covers their right padding and no label wraps anew.
const UserMenuWrapper = styled.div`
  z-index: 15;
  position: fixed;
  top: ${topBarHeight};
  right: 0;
  border-bottom-left-radius: 12px;
  box-shadow: ${surfaces.dockedRightCardShadow};
  width: ${menuWidth};
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    border-radius: inherit;
    background: ${UIStyles.blueColorOpacity};
    -webkit-backdrop-filter: ${surfaces.glassBackdropFilter};
    backdrop-filter: ${surfaces.glassBackdropFilter};
  }
  & > ul {
    max-height: calc(100vh - ${topBarHeight});
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    border-bottom-left-radius: inherit;
  }
  & > ul > li {
    margin-right: calc(100% - ${menuWidth});
  }
  /* Only the mint thumb shows: the global scrollbar paints a blueColor rail. */
  & > ul::-webkit-scrollbar,
  & > ul::-webkit-scrollbar-track {
    background-color: ${UIStyles.transparent};
    box-shadow: none;
  }
`

const SettingsButtonOverride = {
  padding: '10px 20px',
  transition: 'background-color 0.15s',
  '&:hover': {
    background: UIStyles.whiteTransparent01
  }
}

const languageSelectSx = {
  color: UIStyles.whiteColor,
  '& .MuiSelect-select, & .MuiSelect-icon': {
    color: UIStyles.whiteColor
  },
  '&::before, &:hover:not(.Mui-disabled)::before': {
    borderBottomColor: UIStyles.whiteTransparent06
  },
  '&::after': {
    borderBottomColor: UIStyles.whiteColor
  }
}

// The settings drawer is a band of controls: white 10% over the glass.
const SettingsContainer = styled(Collapse)`
  color: ${UIStyles.whiteColor};
  background: ${UIStyles.whiteTransparent01};
`

export const userMenuAnchorCss = css`
  display: block;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  line-height: 24px;
  font-weight: bold;
  color: inherit;
  letter-spacing: 1px;
  padding: 10px 0;
  text-transform: ${props => props.textTransform || 'capitalize'};

  &:hover {
    color: ${UIStyles.whiteColor};
    text-decoration: underline;
  }
`

export function UserMenuLink({ color, ...props }) {
  const StyledLink = styled(Link)`
    ${userMenuAnchorCss}
    cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
    color: ${color || 'inherit'};
    &:focus {
      color: inherit;
    }

    &[disabled] {
      color: ${UIStyles.whiteColor};
      text-decoration: ${({ textDecoration }) => textDecoration || 'none'};
    }
  `
  return <StyledLink {...props} />
}

export const UsermenuLi = styled.li`
  padding: 0 20px;
  color: ${UIStyles.whiteColor};
  /* The global MuiListItemButton override paints labels vomitColor; inherit the menu's color instead. */
  & .MuiListItemButton-root {
    color: inherit;
  }
  /* The language dropdowns render in place (disablePortal) and keep MUI's white paper here instead of the theme's navy
     one: black options with a neutral grey highlight and no mint mark. */
  & .MuiMenu-paper {
    background-color: ${UIStyles.whiteColor};
    border-radius: 4px;
    box-shadow: ${baseTheme.shadows[8]};
  }
  & .MuiMenuItem-root {
    color: ${UIStyles.black};
  }
  & .MuiMenuItem-root:hover,
  & .MuiMenuItem-root.Mui-focusVisible,
  & .MuiMenuItem-root.Mui-selected,
  & .MuiMenuItem-root.Mui-selected:hover,
  & .MuiMenuItem-root.Mui-selected.Mui-focusVisible {
    background: ${UIStyles.lightGrayColor};
    box-shadow: none;
    color: ${UIStyles.black};
  }
`

const SettingsLi = styled(UsermenuLi)`
  padding: 0;
`

// Clickable rows lighten edge to edge on hover (MUI's own hover tint is invisible on navy); logout drops its underline.
const UserMenuRow = styled(UsermenuLi)`
  transition: background-color 0.15s;
  &:hover {
    background: ${UIStyles.whiteTransparent01};
  }
  & .MuiListItemButton-root:hover {
    background: ${UIStyles.transparent};
  }
  & > a:hover {
    text-decoration: none;
  }
`

const UserMenuLiTitle = styled(UsermenuLi)`
  font-size: 25px;
  font-family: 'Roboto', sans-serif;
  font-weight: 300;
  letter-spacing: 1px;
  padding: 10px 20px;
`
export const MenuItem = ({ testId, text, iconName, url, onMenuClose }) => {
  const handleClick = useCallback(() => {
    onMenuClose()
    window.open(url)
  }, [onMenuClose, url])

  return (
    <UserMenuRow data-testid={testId}>
      <ListItemButton onClick={handleClick}>
        <ListItemIcon>
          <i className={iconName} style={{ color: `${themeColors.whiteColor}` }} />
        </ListItemIcon>
        <ListItemText primary={text} />
      </ListItemButton>
    </UserMenuRow>
  )
}

// Passive feedback entry point. Always labelled "Send feedback" — clicking opens the modal.
// Hidden only while analytics is blocked (a submission would never reach Segment).
const FeedbackMenuItem = ({ t, openCsatModal, onMenuClose }) => {
  if (!isCsatAvailable()) return null

  const handleClick = () => {
    onMenuClose()
    openCsatModal()
  }

  return (
    <UserMenuRow data-testid="feedback-button">
      <ListItemButton onClick={handleClick}>
        <ListItemIcon>
          <i className="fa-regular fa-comment-dots" style={{ color: `${themeColors.whiteColor}` }} />
        </ListItemIcon>
        <ListItemText primary={t('Send feedback')} />
      </ListItemButton>
    </UserMenuRow>
  )
}

// Opt-in browser notification when LAYI finishes an answer out of sight (chatbot/layiNotifications.js): the one
// control that turns it on or off. The whole row is the switch, with one click handler (the switch inside only
// draws the state), so a click asks for the browser's permission once. Turning it off keeps the menu open, and so
// does turning it on once this user hid the setup in this browser; any other activation, or a permission the
// browser denies, closes the menu and opens the LAYI notifications modal instead (also when the browser's prompt
// outlived the menu). Keyboard focus shows the invite card's mint ring (the global :focus rule removes outlines,
// and MUI's focus tint is invisible on the glass).
const layiNotificationsRowSx = {
  '&.Mui-focusVisible': {
    outline: `2px solid ${UIStyles.mintColor}`,
    outlineOffset: '-2px'
  }
}

// «Configure», while the notifications are on: a row of its own under the switch (inside it, a click would toggle
// the switch), compact and aligned with the label above, in the soft white of a secondary action.
const layiNotificationsSetupRowSx = {
  ...layiNotificationsRowSx,
  marginTop: 0,
  padding: '0 0 6px',
  '& .MuiListItemText-primary': {
    fontSize: '13px',
    lineHeight: '20px',
    color: UIStyles.whiteTransparent08,
    transition: 'color 0.15s'
  },
  '&:hover .MuiListItemText-primary, &.Mui-focusVisible .MuiListItemText-primary': {
    color: UIStyles.whiteColor
  }
}

const LayiNotificationsMenuItem = ({ t, userId, onMenuClose, openLayiNotificationsModal }) => {
  const labelId = useId()
  const { on, toggle } = useLayiNotificationsSwitch({
    t,
    userId,
    source: 'menu',
    onNeedsModal: ({ blocked }) => {
      onMenuClose()
      openLayiNotificationsModal({ blocked })
    }
  })

  // The setup is enabled again for this user in this browser and opens now; the option and its key stay as they
  // are. If the browser stopped granting the permission since the row last read it, the blocked notice opens
  // instead: the setup's test could not work.
  const configure = () => {
    unhideLayiNotificationsSetup(userId)
    onMenuClose()
    openLayiNotificationsModal({ blocked: !areLayiNotificationsOn() })
  }

  return (
    <>
      <UserMenuRow data-testid="layi-notifications-button">
        <ListItemButton role="switch" aria-checked={on} onClick={toggle} sx={layiNotificationsRowSx}>
          <ListItemIcon>
            <i className="fa-regular fa-bell" style={{ color: `${themeColors.whiteColor}` }} />
          </ListItemIcon>
          <ListItemText id={labelId} primary={t('layi_notifications_menu_label')} />
          <StyledSwitchComponent size="small" checked={on} inputProps={{ style: { display: 'none' } }} />
        </ListItemButton>
      </UserMenuRow>
      {on && (
        <UserMenuRow data-testid="layi-notifications-setup-button">
          <ListItemButton aria-describedby={labelId} onClick={configure} sx={layiNotificationsSetupRowSx}>
            <ListItemText inset primary={t('layi_notifications_configure')} />
          </ListItemButton>
        </UserMenuRow>
      )}
    </>
  )
}

const MenuItemLogOut = ({ text, iconName }) => {
  return (
    <UserMenuRow>
      <UserMenuLink to={`/${LOG_OUT_ROUTE}`} onClick={() => pageViewed(`/${LOG_OUT_ROUTE}`)}>
        <ListItemIcon>
          <i className={iconName} style={{ color: `${themeColors.whiteColor}` }} />
        </ListItemIcon>
        {text}
      </UserMenuLink>
    </UserMenuRow>
  )
}
const UserMenu = ({ userName, userId, locale, t, patchConfig, config, onMenuClose, user, openCsatModal, openLayiNotificationsModal, canUseLayi }) => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)

  return (
    <ClickAwayListener onClickAway={onMenuClose}>
      <UserMenuWrapper>
        <List>
          <UserMenuLiTitle>{userName}</UserMenuLiTitle>

          <MenuItem
            testId="contact-button"
            text={t('Contact')}
            iconName="fa-regular fa-envelope"
            url={`mailto: support@hemav.com?subject=Agro / ${userName}`}
            onMenuClose={onMenuClose}
          />

          <MenuItem
            testId="doc-button"
            text={t('documentation')}
            iconName="fa-solid fa-book-open"
            url={`http://docs.${Routing.locationHostName}/${getDocumentationLocale(locale)}`}
            onMenuClose={onMenuClose}
          />

          <MenuItem testId="support-button" text="Agrosupport" iconName="fa-solid fa-headset" url={freshdeskUrl} onMenuClose={onMenuClose} />

          <MenuItem testId="status-button" text={t('layers status')} iconName="fa-solid fa-heartbeat" url={layersStatusUrl} onMenuClose={onMenuClose} />

          <FeedbackMenuItem t={t} openCsatModal={openCsatModal} onMenuClose={onMenuClose} />

          {canUseLayi && isLayiNotificationsSupported() && (
            <LayiNotificationsMenuItem t={t} userId={userId} onMenuClose={onMenuClose} openLayiNotificationsModal={openLayiNotificationsModal} />
          )}

          <UsermenuLi>
            <LanguageButtons iconColor={UIStyles.whiteColor} selectSx={languageSelectSx} />
          </UsermenuLi>

          <SettingsLi id="settings-menu">
            <ListItemButton sx={[SettingsButtonOverride]} onClick={() => setIsSettingsOpen(!isSettingsOpen)}>
              <ListItemIcon>
                <i className="fa-solid fa-gear" style={{ color: `${themeColors.whiteColor}` }} />
              </ListItemIcon>
              <ListItemText primary={t('settings')} />
              <i
                className={`fa-solid ${isSettingsOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`}
                style={{ marginLeft: '100px', color: `${themeColors.whiteColor}` }}
              />
            </ListItemButton>
            <SettingsContainer in={isSettingsOpen} style={{ padding: 0 }}>
              <UserConfiguration patchConfig={patchConfig} config={config} />
            </SettingsContainer>
          </SettingsLi>

          <MenuItemLogOut text={t('logout')} iconName="fa-solid fa-sign-out-alt" />
        </List>
      </UserMenuWrapper>
    </ClickAwayListener>
  )
}

export default compose(withTranslator, withLocale)(UserMenu)
